import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import React, { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import AppCurrencyInput from '../../../../Component/Common/Components/FormValidationInputs/AppCurrencyInput'
import { AppDatepicker } from '../../../../Component/Common/Components/FormValidationInputs/AppDatepicker'
import AppSelectList from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList'
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput'
import { MDTypography } from '../../../../Component/Common/Components/MDTypography'
import { ProposalValidationSchema } from './ProposalValidationSchema'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { MDButton } from '../../../../Component/Common/Components/MDButton'
import { BBBEECodeList, ProposalAgreementStatusList, ScorecardTypeCategoryList, SectorCodeList, ServiceList } from '../../../../Component/Common/Constants/Lists/DropdownListOptions'
import { useRecoilValue } from 'recoil'
import { VerificationCompanyAtom } from '../../../../State/VerificationCompany.Atom'
import { VerificationAgencyClientApplicationService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService'
import { VerificationAgencyClientApplication } from '../../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication'
import { PortalDocuments } from '../../../Common/PortalDocuments/PortalDocuments'
import { IntermediateGlobalLoading } from '../../../../Component/Common/Components/Loading/IntermediateGlobalLoading'
import { MDBox } from '../../../../Component/Common/Components/MDBox'
import { Box, Grid } from '@mui/material'
import AppYesOrNoSelector from '../../../../Component/Common/Components/FormValidationInputs/AppYesOrNoSelector'
import { IconButton } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect'
import { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList'
import { CoreStaticDataService } from '../../../../Component/Api/StaticData/CoreStaticDataService'
import { accountTypeList } from "../../../../Component/Common/Constants/Lists/DropdownListOptions"
import { VerificationAgencyClientService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService'
import { companyTypeList } from '../../../../Component/Common/Constants/Lists/DropdownListOptions'
import { formatNumber } from '../../../../Component/Model/helpers'

export const Proposals = () => {

  const verificationCompany = useRecoilValue(VerificationCompanyAtom);
  const [proposalId, setProposalId] = React.useState(-1);
  const [documentCount, setDocumentCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [application, setApplication] = React.useState<VerificationAgencyClientApplication.VerificationAgencyClientApplicationResponse>();
  const [showDocumentsPopUp, setShowDocumentsPopUp] = React.useState(false);
  const [claimingBlackOwnership, setClaimingBlackOwnership] = React.useState(false);
  const link = 'Proposals';

  const [serviceRequiredOptions, setServiceRequiredOptions] = React.useState<IDropDownOptions[]>([]);
  const [BBBEECodeOptions, setBBBEECodeOptions] = React.useState<IDropDownOptions[]>([]);
  const [sectorCodeOptions, setSectorCodeOptions] = React.useState<IDropDownOptions[]>([]);
  const [scorecardTypeOptions, setScorecardTypeOptions] = React.useState<IDropDownOptions[]>([]);
  const [ownershipStructureElementsOptions, setOwnershipStructureElementsOptions] = React.useState<IDropDownOptions[]>([]);
  const [proposalCsv, setProposalCsv] = React.useState<VerificationAgencyClientApplication.ProposalCsvDto>();

  let totalDepositRequired = 0
  let totalBalance = 0;

  const { control, watch, handleSubmit, formState: { isSubmitting }, reset, setValue } = useForm({
    mode: 'all',
    resolver: yupResolver(ProposalValidationSchema),
  });

  const proposedFee = watch("proposedFee") || 0;
  const depositRequired = watch("depositRequired") || 0;

  const handleDataSubmit = async (data: FieldValues) => {
    try {
      const formData = { ...data } as VerificationAgencyClientApplication.ProposalDto;
      formData.id = proposalId;
      formData.vaClientApplicationId = verificationCompany.applicationId;

      if (proposalId === -1) {
        const response = await VerificationAgencyClientApplicationService.CreateProposal(formData);
        fetchData()
      } else {
        const response = await VerificationAgencyClientApplicationService.UpdateProposal(formData);
        fetchData()
      }
      toast.success("Proposal saved");
    } catch {
    }
  }

  const resetFormData = (data: VerificationAgencyClientApplication.ProposalDto) => {
    setProposalId(data.id ?? -1);
    if (verificationCompany.applicationId) {
      if (data.proposedFee && data.depositRequired) {
        calcProposalValues(data.proposedFee, data.depositRequired)
        data.totalDepositRequired = totalDepositRequired;
        data.totalBalance = totalBalance;
      }
      var csv = {
        companyName: data.companyDetails?.companyName,
        registrationNumber: data.companyDetails?.registrationNumber,
        vatNumber: data.companyDetails?.vatNumber,
        serviceRequired: ServiceList.find(i=> i.id === data.serviceRequiredAndOwnership?.serviceRequired?.serviceRequired)?.value ?? 'n/a',         
        bbbeeCodes: BBBEECodeList.find(i=> i.id === data.serviceRequiredAndOwnership?.serviceRequired?.bbbeeCode)?.value ?? 'n/a',
        sectorCodes: SectorCodeList.find(i=> i.id === data.serviceRequiredAndOwnership?.serviceRequired?.sectorCode)?.value ?? 'n/a',
        scorecardTypeCategories: ScorecardTypeCategoryList.find(i=> i.id === data.serviceRequiredAndOwnership?.serviceRequired?.scorecardTypeCategory)?.value ?? 'n/a',
        proposedFee: data.proposedFee ?? 'n/a',
        depositRequired: data.depositRequired ?? 'n/a',
        depositAmount: data.totalDepositRequired ?? 'n/a',
        balance: data.totalBalance ?? 'n/a'
      } as VerificationAgencyClientApplication.ProposalCsvDto
      setProposalCsv(csv);
    }
    reset(data);
  }

  const fetchApplication = async () => {
    try {
      const response = await VerificationAgencyClientApplicationService.GetById(verificationCompany.applicationId);
      setApplication(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  const fetchProposalData = async () => {
    try {
      const applicationId = verificationCompany.applicationId;
      const response = await VerificationAgencyClientApplicationService.GetProposalByApplicationId(applicationId);
      setDocumentCount(response.data.documentCount);
      return response.data;
    } catch {
    }
  }

  const fetchStaticData = async () => {
    try {
      const serviceRequiredPromise = CoreStaticDataService.getServicesRequired();
      const BBBEECodesPromise = CoreStaticDataService.getBBBEECodes();
      const sectorCodesPromise = CoreStaticDataService.getSectorCodes();
      const scorecardTypesPromise = CoreStaticDataService.getScorecardTypeCategories();
      const ownershipStructureElementsPromise = CoreStaticDataService.getOwnershipStructureElements();

      const [serviceRequired, BBBEECodes, sectorCodes, scorecardTypes, ownershipStructureElements] = await Promise.all([
        serviceRequiredPromise,
        BBBEECodesPromise,
        sectorCodesPromise,
        scorecardTypesPromise,
        ownershipStructureElementsPromise
      ]);

      const serviceData: IDropDownOptions[] = serviceRequired.data.map(item => {
        return { id: item.id, value: item.name }
      });

      const BBBEECodesData: IDropDownOptions[] = BBBEECodes.data.map(item => {
        return { id: item.id, value: item.name }
      });
      const sectorCodesData: IDropDownOptions[] = sectorCodes.data.map(item => {
        return { id: item.id, value: item.name }
      });
      const scorecardTypeData: IDropDownOptions[] = scorecardTypes.data.map(item => {
        return { id: item.id, value: item.name }
      });
      const ownershipStructureElementsData: IDropDownOptions[] = ownershipStructureElements.data.map(item => {
        return { id: item.id, value: item.name }
      });

      setServiceRequiredOptions(serviceData);
      setBBBEECodeOptions(BBBEECodesData);
      setSectorCodeOptions(sectorCodesData);
      setScorecardTypeOptions(scorecardTypeData);
      setOwnershipStructureElementsOptions(ownershipStructureElementsData);
    } catch (error) {
    }
  }
  const fetchApplicationServiceRequiredData = async () => {
    try {
      const applicationId = verificationCompany.applicationId;
      const response = await VerificationAgencyClientApplicationService.GetServiceRequiredAndOwnershipStructureByApplicationId(applicationId);
      return response.data;
    } catch (error) {
    }
  }
  const fetchFinancialDetails = async () => {
    try {
      const applicationId = verificationCompany.applicationId;
      const response = await VerificationAgencyClientApplicationService.GetFinancialInformation(applicationId);
      return response.data
    } catch (error) {
      console.log(error);
    }
  }
  const fetchCompanyDetails = async () => {
    const id = verificationCompany.vaClientId;
    if (id !== 0) {
      try {
        const response = await VerificationAgencyClientService.GetById(id);
        const companyDetails = {
          vaClientId: verificationCompany.vaClientId,
          companyName: response.data.companyName,
          registrationNumber: response.data.registrationNumber,
          vatNumber: response.data.vatNumber,
          companyTypeId: response.data.companyTypeId
        }
        return companyDetails
      } catch (error) {
        console.log(error);
      }
    }
  }

  const fetchData = async () => {
    setLoading(true);
    await fetchStaticData();
    if (verificationCompany.applicationId !== -1) {
      const applicationPromise = fetchApplication();
      const serviceRequiredPromise = fetchApplicationServiceRequiredData();
      const financialPromise = fetchFinancialDetails();
      const companyDetailsPromise = fetchCompanyDetails();
      const proposalPromise = fetchProposalData();
      const [applicationResponse, serviceRequiredResponse, financialResponse, companyDetailsResponse, proposalResponse] =
        await Promise.all([applicationPromise, serviceRequiredPromise, financialPromise, companyDetailsPromise, proposalPromise]);
      let data = {} as VerificationAgencyClientApplication.ProposalDto;
      if (proposalResponse) {
        data = proposalResponse
      };
      data.serviceRequiredAndOwnership = serviceRequiredResponse;
      setClaimingBlackOwnership(serviceRequiredResponse?.ownershipStructure?.claimingBlackOwnership ?? false);
      data.financialInformation = financialResponse;
      data.companyDetails = companyDetailsResponse;
      resetFormData(data);
    }

    setLoading(false);
  }

  const formatCsvRow = (rowData: any) => {
    return Object.values(rowData).map((value: any) => `${value}`).join(separator) + '\n';
  }

  const separator: string = ",";
  let columHeaders: string[] = ['Company Name', 'Registration Number', 'VAT Number', 'Service Required', 'B-BBEE Code', 'Sub-sector', 'Entity (Scorecard) Type', 'Proposed Fee (Excluding VAT)', 'Deposit Required (%)', 'Total Deposit Required', 'Total Balance After Deposit'];

  const onExportData = async () => {
    setLoading(true);
    try {
      var csvContent = "data:text/csv;charset=utf-8," + columHeaders.join(separator) + '\n' + formatCsvRow(proposalCsv);
      var encodedUri = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download", "Proposal.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }
  }

  const calcProposalValues = (proposedFeeValue: number, depositRequiredValue: number): void => {
    totalDepositRequired = Number((proposedFeeValue * (depositRequiredValue / 100)).toFixed(2));
    totalBalance = proposedFeeValue - totalDepositRequired;
  }

  useEffect(() => {
    fetchData();
  }, [verificationCompany.applicationId]);

  useEffect(() => {
    let proposedFeeValue: number = formatNumber(proposedFee);
    calcProposalValues(proposedFeeValue, depositRequired);
    setValue("totalDepositRequired", totalDepositRequired);
    setValue("totalBalance", totalBalance);
  }, [proposedFee, depositRequired]);

  if (loading) {
    return (<IntermediateGlobalLoading />);
  }
  return (
    <>
      <Box>
        <MDBox style={{ marginBottom: "10px" }}>
          <MDTypography variant='h4' fontWeight='bold'>
            Application - {application?.referenceNumber}
          </MDTypography>
        </MDBox>
        <Grid component="form" id='ProposalForm' onSubmit={handleSubmit(handleDataSubmit)}>
          <Grid item xs={12} container spacing={3} >
            <Grid item xs={6}>
              <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }}>
                Company Details
              </MDTypography>
              <Box mt={2}>
                <AppTextInput control={control} name="companyDetails.companyName" label="Company name" disabled />
              </Box>
              <Box mt={2}>
                <AppSelectList control={control} name="companyDetails.companyTypeId" label="Entity Type" items={companyTypeList} disabled />
              </Box>
              <Box mt={2}>
                <AppTextInput control={control} name="companyDetails.registrationNumber" label="Registration number" disabled />
              </Box>
              <Box mt={2}>
                <AppTextInput control={control} name="companyDetails.vatNumber" label="VAT number" disabled />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }}>
                Financial Information
              </MDTypography>
              <Box mt={2}>
                <AppCurrencyInput control={control} name="financialInformation.turnover" label="Turnover" disabled />
              </Box>
              <Box mt={2}>
                <AppCurrencyInput control={control} name="financialInformation.netProfitAfterTax" label="Net profit after tax" disabled />
              </Box>
              <Box mt={2}>
                <AppSelectList control={control} name="financialInformation.accountType" label="Audited AFS or Management Accounts" items={accountTypeList} disabled />
              </Box>
              <Box mt={2}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <AppTextInput control={control} name="financialInformation.numberOfEmployees" label="Number of employees" disabled />
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <AppDatepicker control={control} name='financialInformation.financialYearEnd' label='Financial year-end' inputFormat='yyyy/MM/dd' disabled />
                      <IconButton>
                        <CalendarTodayIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mt: 2 }}>
            Service Requested
          </MDTypography>
          <Grid item xs={12} container spacing={3}>
            <Grid item xs={6}>
              <Box mt={2}>
                <AppSelectList control={control} name="serviceRequiredAndOwnership.serviceRequired.serviceRequired" label="Service Required" items={serviceRequiredOptions} disabled />
              </Box>
              <Box mt={2}>
                <AppSelectList control={control} name="serviceRequiredAndOwnership.serviceRequired.bbbeeCode" label="B-BBEE Code" items={BBBEECodeOptions} disabled />
              </Box>
              <Box mt={2}>
                <AppSelectList control={control} name="serviceRequiredAndOwnership.serviceRequired.sectorCode" label="Sub-sector" items={sectorCodeOptions} disabled />
              </Box>
              <Box mt={2}>
                <AppSelectList control={control} name="serviceRequiredAndOwnership.serviceRequired.scorecardTypeCategory" label="Entity (Scorecard) Type" items={scorecardTypeOptions} size="medium" disabled />
              </Box>
              <Box mt={2}>
                <AppYesOrNoSelector control={control} name="serviceRequiredAndOwnership.serviceRequired.isPartOfGroupStructure" label='Is the company part of a group structure?' disabled />
              </Box>
              <Box mt={2}>
                <AppTextInput control={control} name="serviceRequiredAndOwnership.serviceRequired.numberOfConsolidatedScorecards" label="Number of entities" disabled />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box mt={2}>
                <AppYesOrNoSelector control={control} name="serviceRequiredAndOwnership.serviceRequired.isRecordsCentralised" label="Are all records / functions Centralised?" disabled />
              </Box>
              <Box mt={2}>
                <AppYesOrNoSelector control={control} name="serviceRequiredAndOwnership.ownershipStructure.claimingBlackOwnership" label="Claiming Black Ownership?" disabled />
              </Box>
              
              {claimingBlackOwnership && 
                <>
                  <Box mt={2}>
                    <AppMultiSelect control={control} name="serviceRequiredAndOwnership.ownershipStructure.ownershipStructureElements" label="Elements included in the ownership structure" options={ownershipStructureElementsOptions} disabled />
                  </Box>
                  <Box mt={2}>
                    <AppYesOrNoSelector control={control} name="serviceRequiredAndOwnership.ownershipStructure.containTrustBboEsop" label="Does the Ownership Structure contain Trusts / BBOSs or ESOPs?" disabled />
                  </Box>
                  <Box mt={2}>
                    <AppTextInput control={control} name="serviceRequiredAndOwnership.ownershipStructure.percentageBlackOwnershipClaimed" label="Percentage Black Ownership Claimed" disabled />
                  </Box>
                </>
              }
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={3}>

            <Grid item xs={6}>
              <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1, mt: 2 }}>
                Proposal Details
              </MDTypography>
              <Box mt={2}>
                <AppCurrencyInput
                  control={control}
                  name="proposedFee"
                  label="Proposed Fee (Excluding VAT)"
                />
              </Box>
              <Box mt={2}>
                <AppTextInput
                  control={control}
                  name="depositRequired"
                  label="Deposit Required (%)"
                />
              </Box>
              <Box mt={2}>
                <AppCurrencyInput
                  control={control}
                  name="totalDepositRequired"
                  label="Total deposit required"
                  defaultValue={0}
                  disabled
                />
              </Box>
              <Box mt={2}>
                <AppCurrencyInput
                  control={control}
                  name="totalBalance"
                  label="Total balance after deposit"
                  defaultValue={0}
                  disabled
                />
              </Box>
              <Grid mt={2}>
                <AppSelectList control={control} name="statusId" label="Status" items={ProposalAgreementStatusList} />
              </Grid>
              <Box mt={2}>
                <AppDatepicker control={control} name="dateSent" label="Date Sent" inputFormat='yyyy/MM/dd' />
              </Box>
              <Box mt={2}>
                <AppDatepicker control={control} name="dateReceived" label="Date Received" inputFormat='yyyy/MM/dd' />
              </Box>
              <Box mt={2}>
                <LoadingButton loading={isSubmitting} variant='contained' form='ProposalForm' type="submit" color='primary' sx={{ mr: 1 }}>
                  Save
                </LoadingButton>
                {proposalId !== -1 &&
                  <MDButton variant="contained" color="primary" onClick={() => setShowDocumentsPopUp(true)} startIcon={<AttachFileIcon />}>
                    Attach File {`(${documentCount})`}
                  </MDButton>
                }
                {proposalId !== -1 &&
                  <MDButton variant="contained" color="primary" onClick={onExportData} startIcon={<DownloadIcon />} sx={{ mb: 1, mt: 1, ml: 1 }}>
                    Export to CSV
                  </MDButton>
                }
              </Box>
            </Grid>
          </Grid>

        </Grid>
      </Box>
      {showDocumentsPopUp &&
        <PortalDocuments display={showDocumentsPopUp} link={link} id={proposalId}
          handleCancel={() => {
            setShowDocumentsPopUp(false);
            fetchData();
          }}
        />}
    </>
  )
}
